import { useEffect } from "react";

export const SITE_URL = "https://www.expresspuntacana.com";
export const SITE_NAME = "Express Punta Cana";

type SeoOptions = {
  title: string;
  description: string;
  /** Path starting with "/" (defaults to current location) */
  path?: string;
  keywords?: string;
  image?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  /** JSON-LD objects rendered inside a single @graph */
  schema?: Record<string, unknown>[];
};

const MANAGED = "data-seo-managed";

function setMeta(key: string, content: string | undefined, property = false) {
  const attr = property ? "property" : "name";
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el?.hasAttribute(MANAGED)) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED, "true");
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    el.setAttribute(MANAGED, "true");
    document.head.appendChild(el);
  }
  el.href = href;
}

/** Business entity reused across every page graph (local SEO / GEO answers). */
export const businessSchema = {
  "@type": ["LocalBusiness", "TravelAgency"],
  "@id": `${SITE_URL}/#business`,
  name: SITE_NAME,
  alternateName: "Express Punta Cana Private Transportation",
  description:
    "Private airport transfers and ground transportation across the Dominican Republic: Punta Cana (PUJ), Santo Domingo (SDQ), La Romana (LRM), Santiago (STI) and Puerto Plata (POP).",
  url: SITE_URL,
  image: `${SITE_URL}/favicon.png`,
  logo: `${SITE_URL}/favicon.png`,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card, PayPal",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Punta Cana",
    addressRegion: "La Altagracia",
    addressCountry: "DO",
  },
  geo: { "@type": "GeoCoordinates", latitude: 18.5601, longitude: -68.3725 },
  areaServed: [
    "Punta Cana",
    "Bávaro",
    "Cap Cana",
    "Uvero Alto",
    "Bayahíbe",
    "La Romana",
    "Santo Domingo",
    "Samaná",
    "Puerto Plata",
    "Santiago",
    "Dominican Republic",
  ].map((name) => ({ "@type": "Place", name })),
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    opens: "00:00",
    closes: "23:59",
  },
};

export const websiteSchema = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}/#business` },
  inLanguage: "en",
};

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function faqSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

export function useSeo({
  title,
  description,
  path,
  keywords,
  image,
  type = "website",
  noindex = false,
  schema = [],
}: SeoOptions) {
  useEffect(() => {
    const pagePath = path ?? window.location.pathname;
    const url = `${SITE_URL}${pagePath === "/" ? "/" : pagePath.replace(/\/$/, "")}`;
    const ogImage = image ?? `${SITE_URL}/og-image.jpg`;

    document.title = title;
    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setMeta("geo.region", "DO-11");
    setMeta("geo.placename", "Punta Cana, Dominican Republic");
    setMeta("geo.position", "18.5601;-68.3725");
    setMeta("ICBM", "18.5601, -68.3725");

    setMeta("og:site_name", SITE_NAME, true);
    setMeta("og:locale", "en_US", true);
    setMeta("og:type", type, true);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", url, true);
    setMeta("og:image", ogImage, true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    setLink("canonical", url);
    setLink("alternate", url, "en");
    setLink("alternate", url, "x-default");

    const graph = [businessSchema, websiteSchema, ...schema];
    let script = document.head.querySelector<HTMLScriptElement>(
      'script[type="application/ld+json"][data-seo-graph]'
    );
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-graph", "true");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, keywords, image, type, noindex, JSON.stringify(schema)]);
}
